import { FC, useEffect, useState } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { Box, TextField } from "@mui/material";
import axios from "../../../utils/axios";

// BatchNumberSelect — free-solo (create-or-select) batch number picker.
//
// Behaviour:
//   - Loads existing batches for (skuId) from GET /erp/batches.
//   - If the user SELECTS an existing batch:
//       → onSelect is called with the full batch object.
//       → mfgDate + expiryDate are auto-filled and **locked** (the lot's own dates).
//   - If the user TYPES a NEW number (not in the list):
//       → onSelect is called with { batchNumber, isNew: true }.
//       → The parent renders editable date fields.
//   - Dates are formatted as YYYY-MM-DD for <input type="date">.

type BatchOption = {
  batchNumber: string;
  expiryDate?: string | null;
  mfgDate?: string | null;
  salePrice?: number;
  purchasePrice?: number;
  _id?: string;
  isNew?: boolean;
};

interface Props {
  skuId?: string;
  value?: string; // controlled batchNumber string
  onSelect: (batch: BatchOption | null) => void;
  disabled?: boolean;
}

const filter = createFilterOptions<BatchOption>();
const toDateStr = (d: any): string => (d ? new Date(d).toISOString().slice(0, 10) : "");

const BatchNumberSelect: FC<Props> = ({ skuId, value, onSelect, disabled }) => {
  const [options, setOptions] = useState<BatchOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!skuId) { setOptions([]); return; }
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/v1/erp/batches?skuId=${skuId}`);
        if (active) {
          const list = (Array.isArray(res.data) ? res.data : []).map((b: any) => ({
            ...b,
            expiryDate: toDateStr(b.expiryDate),
            mfgDate: toDateStr(b.mfgDate),
          }));
          setOptions(list);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [skuId]);

  const selected: BatchOption | null = value
    ? options.find((o) => o.batchNumber === value) || { batchNumber: value }
    : null;

  // Lot status shown as a small caption above the field, on the right — mirrors
  // the floating "Batch no." label on the left. Title-cased.
  const statusText = selected && !selected.isNew && selected._id ? "Existing Lot" : "New Lot";

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        component="span"
        sx={{
          position: "absolute",
          top: -9,
          right: 4,
          zIndex: 1,
          px: 0.5,
          bgcolor: "#fff",
          fontSize: 11,
          lineHeight: 1,
          color: "#7a8290",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        {statusText}
      </Box>
    <Autocomplete<BatchOption, false, false, true>
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      loading={loading}
      disabled={disabled}
      options={options}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.batchNumber || "")}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const input = params.inputValue.trim();
        // Offer "Create new batch: <input>" if it doesn't already match an existing one.
        if (input && !opts.find((o) => o.batchNumber === input)) {
          filtered.push({ batchNumber: input, isNew: true });
        }
        return filtered;
      }}
      renderOption={(props, option) => {
        const label = option.isNew
          ? `➕ New lot: "${option.batchNumber}"`
          : option.expiryDate
          ? `${option.batchNumber}  ·  Exp ${option.expiryDate}`
          : option.batchNumber;
        return <li {...props} key={option._id || option.batchNumber}>{label}</li>;
      }}
      value={selected}
      onChange={(_e, newValue) => {
        if (newValue === null) {
          onSelect(null);
        } else if (typeof newValue === "string") {
          onSelect({ batchNumber: newValue, isNew: true });
        } else {
          onSelect(newValue);
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label="Batch no." size="small" />
      )}
    />
    </Box>
  );
};

export default BatchNumberSelect;
